/**
 * AwaitingEmailSection - Displays email forwarding UI while waiting for inbound email.
 *
 * Shows:
 * - Email address with copy button (before email arrives)
 * - "Waiting for email..." spinner
 * - "Email received!" + "Extracting..." (after email arrives)
 * - Error state with details (if no booking found)
 * - "Upload a file instead" back link
 */

import { motion } from 'framer-motion';
import { Mail, Check, Loader2, AlertCircle, Copy, ArrowLeft, FileText } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface AwaitingEmailSectionProps {
  emailAddress: string;
  emailReceived: boolean;
  emailSubject: string | null;
  emailFrom: string | null;
  isExtracting: boolean;
  error: string | null;
  errorDetails: {
    reason: string;
    attachmentsFound: Array<{
      filename: string;
      category: string;
      type: string;
    }>;
  } | null;
  onBack: () => void;
}

export function AwaitingEmailSection({
  emailAddress,
  emailReceived,
  emailSubject,
  emailFrom,
  isExtracting,
  error,
  errorDetails,
  onBack,
}: AwaitingEmailSectionProps) {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopied(true);
      toast.success('E-Mail-Adresse kopiert!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('E-Mail-Adresse konnte nicht kopiert werden');
    }
  };

  return (
    <motion.div
      key="awaiting-email-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center justify-center min-h-[70vh] max-w-lg mx-auto px-4"
    >
      {/* Status icon */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className={cn(
          'w-20 h-20 rounded-2xl flex items-center justify-center mb-8 shadow-lg',
          error
            ? 'bg-red-100 shadow-red-500/20'
            : emailReceived
              ? 'bg-emerald-100 shadow-emerald-500/20'
              : 'bg-blue-100 shadow-blue-500/20'
        )}
      >
        {error ? (
          <AlertCircle className="w-10 h-10 text-red-600" />
        ) : emailReceived ? (
          isExtracting ? (
            <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
          ) : (
            <Check className="w-10 h-10 text-emerald-600" />
          )
        ) : (
          <Mail className="w-10 h-10 text-blue-600" />
        )}
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-2xl font-bold text-slate-900 text-center mb-3"
      >
        {error
          ? 'Keine Buchung gefunden'
          : emailReceived
            ? isExtracting
              ? 'Buchungsdetails werden extrahiert...'
              : 'E-Mail empfangen!'
            : 'Leiten Sie Ihre Buchungsbestätigung weiter'}
      </motion.h2>

      {/* Description */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-slate-500 text-center mb-8"
      >
        {error
          ? "Wir konnten keine Buchungsbestätigung in Ihrer E-Mail finden."
          : emailReceived
            ? emailSubject
              ? `Wird verarbeitet: "${emailSubject}"`
              : 'Wir haben Ihre E-Mail erhalten und extrahieren die Buchungsdetails.'
            : 'Leiten Sie Ihre Flugbuchungsbestätigung weiter an:'}
      </motion.p>

      {/* Email address card (before email arrives) */}
      {!emailReceived && !error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25 }}
          className="w-full bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm"
        >
          <div className="flex items-center justify-between gap-4">
            <code className="text-lg font-mono text-blue-600 break-all select-all">
              {emailAddress}
            </code>
            <button
              onClick={copyEmail}
              className="flex-shrink-0 p-2 rounded-lg hover:bg-slate-100 transition-colors"
              title="E-Mail-Adresse kopieren"
            >
              {copied ? (
                <Check className="w-5 h-5 text-emerald-600" />
              ) : (
                <Copy className="w-5 h-5 text-slate-400" />
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* Email received confirmation */}
      {emailReceived && emailFrom && !error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full bg-emerald-50 rounded-xl border border-emerald-200 p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-700">E-Mail empfangen von</p>
              <p className="text-sm text-emerald-600">{emailFrom}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Waiting animation */}
      {!emailReceived && !error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-2 text-slate-400 mb-8"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Warte auf E-Mail...</span>
        </motion.div>
      )}

      {/* Error state */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full space-y-4 mb-6"
        >
          <div className="p-4 rounded-xl bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>

          {/* Show what was found */}
          {errorDetails?.attachmentsFound && errorDetails.attachmentsFound.length > 0 && (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                Wir haben stattdessen diese Dateien gefunden:
              </p>
              <div className="space-y-2">
                {errorDetails.attachmentsFound.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span>{file.filename}</span>
                    {file.category && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                        {file.category.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Instructions (before email arrives) */}
      {!emailReceived && !error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="w-full bg-slate-50 rounded-xl p-4 mb-8"
        >
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
            So funktioniert es
          </p>
          <ol className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-medium">
                1
              </span>
              <span>Öffnen Sie Ihre Buchungsbestätigungs-E-Mail in Ihrer E-Mail-App</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-medium">
                2
              </span>
              <span>Leiten Sie diese an die obige E-Mail-Adresse weiter</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-medium">
                3
              </span>
              <span>Wir extrahieren automatisch Ihre Flugdetails</span>
            </li>
          </ol>
        </motion.div>
      )}

      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Stattdessen Datei hochladen</span>
      </motion.button>
    </motion.div>
  );
}
